import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { useDbUser } from '../hooks/useDbUser';
import { useLocalTimer } from '../hooks/useLocalTimer';
import versionData from '../../version.json';
import CurrentTaskPanel from './focus/CurrentTaskPanel';
import TaskQueuePanel from './focus/TaskQueuePanel';
import { useFocusModeController } from './focus/useFocusModeController';

function FocusModeV2() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isRunning: isTimerRunning,
    currentTaskId: timerTaskId,
    startTimer,
    stopTimer,
    forceClearTimer
  } = useLocalTimer();

  const focusController = useFocusModeController({
    dbUserId,
    navigate,
    location,
    isTimerRunning,
    timerTaskId,
    startTimer,
    stopTimer,
    forceClearTimer
  });

  const {
    loading,
    taskQueue,
    currentTask,
    notes,
    setNotes,
    recentTimeEntries,
    completingTask,
    isEditingSOP,
    startSopEditing,
    cancelSopEditing,
    editedSOP,
    setEditedSOP,
    handleLoadTimer,
    handleStopTimer,
    handleCompleteTask,
    handleBumpTask,
    handleDeferToTomorrow,
    handleSaveSOP
  } = focusController;

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    console.log(`📍 FocusModeV2 loaded - v${versionData.version}`);
  }, []);

  useEffect(() => {
    if (!dbUserLoading && !loading) {
      setHasLoadedOnce(true);
    }
  }, [dbUserLoading, loading]);

  const showBlockingOverlay = hasLoadedOnce && (dbUserLoading || loading);

  if (!hasLoadedOnce) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-600">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span>Loading focus mode…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Focus Mode</h1>
            <p className="text-sm text-gray-500 mt-1">{taskQueue.length} task{taskQueue.length !== 1 ? 's' : ''} in queue</p>
          </div>
          <button
            onClick={() => navigate('/overview')}
            className="p-2 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <CurrentTaskPanel
          task={currentTask}
          notes={notes}
          onNotesChange={setNotes}
          timeEntries={recentTimeEntries}
          isTimerRunning={isTimerRunning}
          timerTaskId={timerTaskId}
          onStartTimer={handleLoadTimer}
          onStopTimer={handleStopTimer}
          onCompleteTask={handleCompleteTask}
          onBumpTask={handleBumpTask}
          onDeferToTomorrow={handleDeferToTomorrow}
          completingTask={completingTask}
          isEditingSOP={isEditingSOP}
          editedSOP={editedSOP}
          onStartEditingSOP={startSopEditing}
          onUpdateSOP={setEditedSOP}
          onCancelEditSOP={cancelSopEditing}
          onSaveSOP={handleSaveSOP}
        />

        <TaskQueuePanel tasks={taskQueue} currentTaskId={currentTask?.id} />

        {!currentTask && (
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks available</h3>
            <p className="text-gray-500 mb-4">You've completed all your tasks for now!</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-blue-600 hover:text-blue-500"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
      {showBlockingOverlay && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 text-gray-600">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span>Updating your focus queue…</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default FocusModeV2;
